/**
 * "This was wrong" — reported inline, at the moment of the mistake.
 *
 * Budget: one click plus one sentence. Anything heavier and corrections stop
 * happening, which is the same failure mode as review fatigue.
 *
 * `wrong_text` is READ-ONLY on purpose. It is what the engine actually said; an
 * editable copy would stop being a factual record of the mistake.
 */
import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { reportCorrection, getSessionSymptom, getSessionSymptomCached } from "./useLearning";

const CorrectionComposer = ({ sessionId, turnNo, symptomText, wrongText, wrongNids = [], onDone }) => {
  // Instant render: the cache holds the last-resolved symptom (or null on the
  // very first open). Fall back to the prop — the engineer's opening line — so
  // the field is never blank.
  const cached = getSessionSymptomCached(sessionId);
  const [symptom, setSymptom] = useState(
    (cached?.symptom_text || "").trim() || symptomText || ""
  );
  const [symptomTouched, setSymptomTouched] = useState(false);

  // The symptom shown here must be the SAME one the learning trail carries, not
  // the raw first message. `symptomText` is the engineer's opening line, frozen
  // at session start ("facing slowness with ZCC"); the trail holds the engine's
  // normalised statement, refreshed every turn by the per-turn capture. Showing
  // two different symptoms for one session made the correction and the trail
  // disagree about what the case even was.
  //
  // Stale-while-revalidate: the cache gave us an instant value above, and this
  // effect ALWAYS hits the network to get the current one. If the fresh value
  // differs from what is on screen (and the engineer has not edited the field),
  // we swap it in. This is what the old code failed to do — the previous
  // implementation returned the stale cached value from getSessionSymptom and
  // never delivered the revalidated result to the component.
  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        // Cheap single-string endpoint, not the whole trail — this dialog is
        // raised from a live chat and was measured taking 1-2s over the full
        // trail read; the symptom-only endpoint gets this to one round trip.
        // The sync cache above already populated the field instantly; this
        // fetch is the revalidation half of stale-while-revalidate.
        const data = await getSessionSymptom(sessionId);
        const canonical = (data?.symptom_text || "").trim();
        if (cancelled || !canonical || symptomTouched) return;
        setSymptom((prev) => (prev.trim() === canonical ? prev : canonical));
      } catch {
        // No stored trail yet, or learning is switched off — the opening line
        // stands. A correction must never be blocked by the learning module.
      }
    })();
    return () => {
      cancelled = true;
    };
    // symptomTouched is read, not depended on: a late answer must not clobber
    // an edit the engineer made while it was in flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
  const [why, setWhy] = useState("");
  const [correct, setCorrect] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  // Closing silently reads as "did that go anywhere?", and an engineer who is
  // not sure their report landed stops sending them. Acknowledge, then close.
  useEffect(() => {
    if (!sent) return undefined;
    const t = setTimeout(() => onDone?.(true), 1400);
    return () => clearTimeout(t);
  }, [sent, onDone]);

  const submit = async () => {
    if (!why.trim()) return;
    setBusy(true);
    setError("");
    try {
      await reportCorrection({
        session_id: sessionId,
        turn_no: turnNo,
        symptom_text: symptom,
        wrong_text: wrongText,
        wrong_nids: wrongNids,
        why_wrong: why.trim(),
        correct_text: correct.trim() || null,
      });
      setSent(true);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-8 text-center">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white">
          <Check size={18} strokeWidth={3} />
        </span>
        <p className="text-[13.5px] font-semibold text-[#253439]">Thanks — sent for review</p>
        <p className="text-[12px] text-gray-500">
          Nothing changes in this session. It takes effect once an engineer publishes it.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3">
      <p className="mb-2 text-[13px] font-semibold text-[#253439]">What went wrong?</p>

      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        Symptom
      </label>
      <input
        value={symptom}
        onChange={(e) => {
          setSymptomTouched(true);
          setSymptom(e.target.value);
        }}
        className="mb-2 w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-[13px] outline-none focus:border-cyan-400"
      />

      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        The engine said
      </label>
      <p className="mb-2 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-[12.5px] text-gray-500">
        {wrongText}
      </p>

      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-rose-700">
        Why it&rsquo;s wrong *
      </label>
      <textarea
        value={why}
        onChange={(e) => setWhy(e.target.value)}
        rows={2}
        placeholder="One sentence is enough."
        className="mb-2 w-full resize-none rounded-md border border-gray-200 px-2.5 py-1.5 text-[13px] outline-none focus:border-cyan-400"
      />

      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        What was right (optional)
      </label>
      <input
        value={correct}
        onChange={(e) => setCorrect(e.target.value)}
        className="mb-3 w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-[13px] outline-none focus:border-cyan-400"
      />

      {error && <p className="mb-2 text-[12px] text-rose-700">{error}</p>}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => onDone?.(false)}
          className="rounded-md border border-gray-200 px-3 py-1.5 text-[12.5px] text-gray-500 hover:border-gray-400"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!why.trim() || busy}
          className="rounded-md bg-[#253439] px-4 py-1.5 text-[12.5px] font-semibold text-white disabled:opacity-40"
        >
          {busy ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
};

export default CorrectionComposer;
